import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { dateFormatter } from '../utils/date-format';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';

export const MY_FORMATS = {
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'date-picker',
  template: `
    <div
      [ngClass]="{
        'flex flex-row items-center gap-2 mb-4': position === 'horizontal',
        'mb-4': position === 'vertical',
        'mb-0': position === null,
      }"
    >
      <label
        [for]="controlName"
        class="text-sm font-medium text-gray-700 flex items-center gap-1"
      >
        {{ label }}
        <span *ngIf="isRequired() && label !== ''" class="text-red-500">
          *
        </span>

        <mat-icon
          *ngIf="tooltipText"
          svgIcon="mat_outline:info"
          [matTooltip]="tooltipText"
          [matTooltipPosition]="tooltipPosition"
          [matTooltipShowDelay]="showDelay"
          [matTooltipHideDelay]="hideDelay"
          class="text-grey-500 cursor-pointer icon-size-5 relative top-[2px]"
        ></mat-icon>
      </label>

      <div
        *ngIf="type === 'default'; else customStyle"
        class="flex flex-row justify-between mt-1 w-full h-8 border border-gray-300 px-4 items-center rounded-md"
      >
        <input
          class="text-black flex w-full cursor-pointer"
          autocomplete="off"
          matInput
          [matDatepicker]="date"
          [placeholder]="placeholder"
          [id]="controlName"
          [name]="controlName"
          [formControl]="controlName"
          [matDatepickerFilter]="dateFilter"
          (click)="date.open()"
          readonly
        />
        <mat-datepicker #date (closed)="onDateSelected()"></mat-datepicker>
      </div>

      <ng-template #customStyle>
        <div
          class="flex border-blue-500 box-border h-9 w-40 items-center border-2 rounded-md relative"
        >
          <input
            matInput
            [matDatepicker]="date"
            [placeholder]="placeholder"
            [id]="controlName"
            [name]="controlName"
            [formControl]="controlName"
            class="w-full text-center cursor-pointer"
            [matDatepickerFilter]="dateFilter"
            (click)="date.open()"
            readonly
          />
          <mat-datepicker #date (closed)="onDateSelected()"></mat-datepicker>
        </div>
      </ng-template>

      <div
        class="invalid-feedback"
        *ngIf="controlName.invalid && controlName.touched"
      >
        <div
          *ngIf="controlName.errors?.required"
          class="text-sm text-red-500 mt-1 ml-2"
        >
          This field is required.
        </div>
        <div
          *ngIf="controlName.errors?.dateRange"
          class="text-sm text-red-500 mt-1 ml-2"
        >
          This field must be within the valid date range.
        </div>
        <div
          *ngIf="controlName.errors?.minDate"
          class="text-sm text-red-500 mt-1 ml-2"
        >
          End date cannot be earlier than start date.
        </div>
      </div>
    </div>
  `,
  providers: [
    DatePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MY_FORMATS,
    },
  ],
})
export class DatePickerComponent implements OnInit {
  @Input() label: string = '';
  @Input() controlName: FormControl = new FormControl('');
  @Input() placeholder: string = 'DD/MM/YYYY';
  @Input() type: string = 'default';
  @Input() position: string = 'vertical';
  @Input() dateFilter: (date: Date | null) => boolean = () => true;
  @Output() onSelect: EventEmitter<any> = new EventEmitter<any>();
  @Input() tooltipText: string = '';
  @Input() tooltipPosition: 'above' | 'below' | 'left' | 'right' = 'above';
  @Input() showDelay: number = 50;
  @Input() hideDelay: number = 50;

  constructor(private datePipe: DatePipe) {}

  public onDateSelected(): void {
    this.controlName.setValue(
      dateFormatter(this.datePipe, this.controlName.value, 'yyyy-MM-dd')
    );
    this.onSelect.emit(this.controlName.value);
  }

  ngOnInit(): void {
    if (this.controlName.value) {
      this.onDateSelected();
    }
  }

  public isRequired(): boolean {
    return this.controlName.hasValidator(Validators.required);
  }
}
